import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Registro mínimo de "hay un modal abierto en la app". Cada modal maneja su
 * propio isOpen local; los que deben retirar el checklist se registran con
 * useTrackModal. Como mínimo: el modal de venta exitosa (ya lo hace) y los
 * modales/drawers de crear producto y crear cliente, que son justo los que
 * abre el checklist y suelen tener sus botones abajo a la derecha.
 */
interface ModalTrackerState {
  openModals: Record<string, boolean>;
  setModalOpen: (id: string, open: boolean) => void;
}

export const useModalTrackerStore = create<ModalTrackerState>((set) => ({
  openModals: {},
  setModalOpen: (id, open) =>
    set((s) => ({ openModals: { ...s.openModals, [id]: open } })),
}));

export const useHasOpenModal = (): boolean =>
  useModalTrackerStore((s) => Object.values(s.openModals).some(Boolean));

/**
 * Registra un modal o drawer mientras está abierto. Una línea por modal:
 *
 *   useTrackModal('crear-producto', isOpen);
 *
 * Se desregistra solo al cerrarse o desmontarse.
 */
export function useTrackModal(id: string, open: boolean): void {
  const setModalOpen = useModalTrackerStore((s) => s.setModalOpen);
  useEffect(() => {
    setModalOpen(id, open);
    return () => setModalOpen(id, false);
  }, [id, open, setModalOpen]);
}
